import { useCallback, useEffect, useState } from "react";
import { Download, Eye, FileText, Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate, money } from "@/lib/format";
import { EmptyState, Field, Loader, StatusPill } from "@/components/common";
import { Confirm, FormDialog } from "@/components/kids/kidsUi";
import FilePreview from "@/components/FilePreview";
import { ACCEPT, checkFile, fmtSize, saveBlob, uploadFile } from "@/lib/upload13";

const ORANGE = "var(--glow-home)";
const BADGE_RGB = (b) => (!b ? null : b === "Under warranty" ? "var(--glow-money)" : b === "Out of warranty" ? "var(--glow-media)" : "var(--glow-lists)");

function ApplianceDialog({ hubId, types, item, onClose, onSaved }) {
  const [f, setF] = useState(() => ({ name: item?.name || "", type: item?.type || "Appliance", brand: item?.brand || "", model: item?.model || "", serial: item?.serial || "",
    purchase_date: item?.purchase_date || "", price: item?.price ?? "", store: item?.store || "", warranty_end: item?.warranty_end || "", warranty_months: item?.warranty_months || "", manual_url: item?.manual_url || "", filter_size: item?.filter_size || "", filter_months: item?.filter_months || "" }));
  const [useMonths, setUseMonths] = useState(!!item?.warranty_months);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setBusy(true);
    const body = { ...f, purchase_date: f.purchase_date || null, price: f.price === "" ? null : Number(f.price),
      warranty_end: useMonths ? null : f.warranty_end || null, warranty_months: useMonths && f.warranty_months ? Number(f.warranty_months) : null, filter_months: f.filter_months ? Number(f.filter_months) : null };
    try {
      if (item) await api.put(`/organizations/${hubId}/home/appliances/${item.id}`, body); else await api.post(`/organizations/${hubId}/home/appliances`, body);
      toast.success("Saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const input = (k, label, type = "text", ph = "") => <Field label={label}><input type={type} className="q-input w-full" value={f[k]} placeholder={ph} onChange={(e) => set(k, e.target.value)} data-testid={`appliance-${k}-input`} /></Field>;
  return (
    <FormDialog open onClose={onClose} title={item ? `Edit ${item.name}` : "Add an appliance or vehicle"} testid="appliance-dialog">
      <div className="space-y-3">
        {input("name", "Name", "text", "Samsung fridge")}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type"><select className="q-input w-full" value={f.type} onChange={(e) => set("type", e.target.value)} data-testid="appliance-type-select">{types.map((t) => <option key={t}>{t}</option>)}</select></Field>
          {input("brand", "Brand")}
          {input("model", "Model")}
          {input("serial", f.type === "Vehicle" ? "VIN" : "Serial number")}
          {input("purchase_date", "Purchase date", "date")}
          {input("price", "Price", "number")}
        </div>
        {input("store", "Store")}
        <div className="flex gap-2">
          {[[false, "Warranty end date"], [true, "Warranty length"]].map(([m, l]) => (
            <button key={l} className={`q-btn min-h-[44px] flex-1 ${useMonths === m ? "q-btn-primary" : "q-btn-secondary"}`} onClick={() => setUseMonths(m)} data-testid={`appliance-warranty-mode-${m ? "months" : "date"}`}>{l}</button>
          ))}
        </div>
        {useMonths ? input("warranty_months", "Warranty length in months (counts from the purchase date)", "number") : input("warranty_end", "Warranty ends", "date")}
        {input("manual_url", "Manual link (optional)", "url", "https://")}
        {f.type === "Appliance" && <div className="grid grid-cols-2 gap-3">{input("filter_size", "HVAC filter size (optional)", "text", "16x25x1")}{input("filter_months", "Replace every (months)", "number")}</div>}
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="appliance-save-btn">{busy ? "Saving…" : "Save"}</button>
      </div>
    </FormDialog>
  );
}

function ApplianceDetail({ hubId, id, canDelete, onClose, onEdit, onChanged }) {
  const [a, setA] = useState(null);
  const [view, setView] = useState(null);
  const [kind, setKind] = useState("receipt");
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(false);
  const base = `/organizations/${hubId}/home`;
  const load = useCallback(() => api.get(`${base}/appliances/${id}`).then(({ data }) => setA(data)).catch((e) => toast.error(errMsg(e))), [base, id]);
  useEffect(() => { load(); }, [load]);
  const [pick, setPick] = useState(null);
  const docBase = `/organizations/${hubId}/documents`;
  const blob = useCallback((f, dl) => api.get(`${docBase}/${f.doc_id}/files/${f.id}`, { params: { download: dl }, responseType: "blob" }).then((r) => r.data), [docBase]);
  const openPicker = () => api.get(docBase).then(({ data }) => setPick(data.documents.filter((x) => !a.documents.some((y) => y.id === x.id)))).catch((e) => toast.error(errMsg(e)));
  const link = (docId) => api.post(`${base}/appliances/${id}/documents`, { doc_id: docId }).then(({ data }) => { setA(data); setPick(null); toast.success("Attached"); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  const unlink = (docId) => api.delete(`${base}/appliances/${id}/documents/${docId}`).then(({ data }) => { setA(data); setView(null); toast.success("Removed from this item. It stays in Documents."); onChanged(); }).catch((e) => toast.error(errMsg(e)));
  const add = async (e) => {
    const files = [...e.target.files]; e.target.value = "";
    for (const f of files) {
      const bad = checkFile(f); if (bad) { toast.error(bad); continue; }
      setBusy(true);
      try { const uid = await uploadFile(base, f); await api.post(`${base}/appliances/${id}/files`, { upload_id: uid, kind }); toast.success(`${f.name} added`); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
    }
    load(); onChanged();
  };
  const remove = async () => { try { await api.delete(`${base}/appliances/${id}`); toast.success("Deleted"); onChanged(); onClose(); } catch (e) { toast.error(errMsg(e)); } };
  const viewLoad = useCallback(() => blob(view, false), [blob, view]);
  return (
    <FormDialog open onClose={onClose} title={a?.name || "Item"} testid="appliance-detail">
      {!a ? <Loader /> : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {a.badge && <StatusPill label={a.badge} rgb={BADGE_RGB(a.badge)} testid="appliance-detail-badge" />}
            <span className="text-sm text-q-muted">{[a.type, a.brand, a.model].filter(Boolean).join(" · ")}</span>
          </div>
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {[["Serial / VIN", a.serial], ["Purchased", a.purchase_date && fmtDate(a.purchase_date)], ["Price", a.price != null && money(a.price)], ["Store", a.store], ["Warranty ends", a.warranty_end && fmtDate(a.warranty_end)], ["Filter size", a.filter_size]].filter(([, v]) => v).map(([k, v]) => (
              <div key={k}><dt className="text-xs text-q-muted">{k}</dt><dd className="break-words text-q-text">{v}</dd></div>
            ))}
          </dl>
          {a.filter_reminder && <p className="rounded-xl bg-q-raised p-3 text-sm font-semibold text-q-text" data-testid="appliance-filter-reminder">{a.filter_reminder}</p>}
          {a.manual_url && <a href={a.manual_url} target="_blank" rel="noreferrer" className="text-sm font-semibold underline" style={{ color: `rgb(${ORANGE})` }} data-testid="appliance-manual-link">Open the manual</a>}
          <div>
            <p className="q-label mb-2">Documents</p>
            {!a.documents.length && <p className="text-sm text-q-muted" data-testid="appliance-docs-empty">No documents yet. Add a receipt, manual or photo; it's filed once in Documents.</p>}
            <ul className="space-y-2" data-testid="appliance-docs">
              {a.documents.map((doc) => (
                <li key={doc.id} className="rounded-xl bg-q-raised p-2" data-testid={`appliance-doc-${doc.id}`}>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-q-muted" />
                    <span className="min-w-0 flex-1 truncate text-sm font-semibold text-q-text">{doc.sensitive ? "Sensitive document (open it in Documents)" : doc.title} <span className="text-xs font-normal text-q-muted">· {doc.category}</span></span>
                    <button className="q-btn q-btn-ghost min-h-[44px]" onClick={() => unlink(doc.id)} data-testid={`appliance-doc-remove-${doc.id}`}>Remove</button>
                  </div>
                  {doc.files.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 pl-6" data-testid={`appliance-file-${f.id}`}>
                      <span className="min-w-0 flex-1 truncate text-xs text-q-muted">{f.name} · {fmtSize(f.size)}</span>
                      <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label="View" onClick={() => setView({ ...f, doc_id: doc.id })} data-testid={`appliance-file-view-${f.id}`}><Eye className="h-4 w-4" /></button>
                      {doc.my_mode !== "view" && <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px]" aria-label="Download" onClick={() => blob({ ...f, doc_id: doc.id }, true).then((b) => saveBlob(b, f.name))} data-testid={`appliance-file-download-${f.id}`}><Download className="h-4 w-4" /></button>}
                    </div>
                  ))}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <select className="q-input min-h-[44px]" value={kind} onChange={(e) => setKind(e.target.value)} data-testid="appliance-file-kind">{["receipt", "manual", "photo"].map((k) => <option key={k}>{k}</option>)}</select>
              <label className="q-btn q-btn-secondary min-h-[44px] cursor-pointer" data-testid="appliance-file-add">
                <Paperclip className="h-4 w-4" /> {busy ? "Uploading…" : "Add file"}
                <input type="file" multiple accept={ACCEPT} className="hidden" onChange={add} data-testid="appliance-file-input" />
              </label>
              <button className="q-btn q-btn-secondary min-h-[44px]" onClick={openPicker} data-testid="appliance-attach-existing">Attach existing</button>
            </div>
            {pick && (
              <div className="mt-2 rounded-xl border border-q-line p-2" data-testid="attach-picker">
                {!pick.length ? <p className="text-sm text-q-muted">No other documents to attach.</p> : pick.map((x) => (
                  <button key={x.id} className="flex min-h-[44px] w-full items-center rounded-lg px-2 text-left text-sm text-q-text hover:bg-q-raised" onClick={() => link(x.id)} data-testid={`attach-pick-${x.id}`}>{x.title} <span className="ml-1 text-xs text-q-muted">· {x.category}</span></button>
                ))}
              </div>
            )}
          </div>
          {view && <div data-testid="appliance-file-viewer"><FilePreview load={viewLoad} file={view} /></div>}
          {a.tasks.length > 0 && (
            <div>
              <p className="q-label mb-2">Linked tasks</p>
              <ul className="space-y-1 text-sm" data-testid="appliance-tasks">
                {a.tasks.map((t) => <li key={t.id} className="text-q-text">{t.icon} {t.title} <span className="text-q-muted">· {t.due ? `due ${fmtDate(t.due)}` : "completed"}{t.history[0] ? ` · last done ${fmtDate(t.history[0].date)}` : ""}</span></li>)}
              </ul>
            </div>
          )}
          <div className="flex gap-2">
            <button className="q-btn q-btn-secondary min-h-[44px] flex-1" onClick={() => onEdit(a)} data-testid="appliance-edit-btn"><Pencil className="h-4 w-4" /> Edit</button>
            {canDelete && <button className="q-btn q-btn-danger min-h-[44px]" onClick={() => setDel(true)} data-testid="appliance-delete-btn"><Trash2 className="h-4 w-4" /> Delete</button>}
          </div>
          <Confirm open={del} onClose={() => setDel(false)} title={`Delete ${a.name}?`} body="Its documents stay in Documents. Linked tasks stay." onConfirm={remove} testid="appliance-delete-confirm" />
        </div>
      )}
    </FormDialog>
  );
}

export default function Appliances({ hub }) {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const [open, setOpen] = useState(null);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/home/appliances`).then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))), [hub.id]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Loader />;
  return (
    <div data-testid="appliances-tab">
      <button className="q-btn q-btn-primary mb-4 min-h-[44px]" onClick={() => setEdit({})} data-testid="appliance-add-btn"><Plus className="h-4 w-4" /> Add item</button>
      {!d.appliances.length ? <EmptyState title="No appliances or vehicles yet" hint="Add your fridge, washer or car to keep receipts and warranties in one place." rgb={ORANGE} testid="appliances-empty" /> : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {d.appliances.map((a) => (
            <button key={a.id} className="q-card q-rise min-h-[44px] p-4 text-left transition-transform duration-150 hover:-translate-y-0.5" onClick={() => setOpen(a.id)} data-testid={`appliance-${a.id}`}>
              <p className="font-semibold text-q-text">{a.name}</p>
              <p className="text-xs text-q-muted">{[a.type, a.brand, a.model].filter(Boolean).join(" · ")}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {a.badge && <StatusPill label={a.badge} rgb={BADGE_RGB(a.badge)} testid={`appliance-badge-${a.id}`} />}
                <span className="text-xs text-q-muted">{a.documents.length} document{a.documents.length === 1 ? "" : "s"} · {a.task_count} task{a.task_count === 1 ? "" : "s"}</span>
              </div>
            </button>
          ))}
        </div>
      )}
      {open && <ApplianceDetail hubId={hub.id} id={open} canDelete={d.can_delete} onClose={() => setOpen(null)} onEdit={(a) => { setOpen(null); setEdit(a); }} onChanged={load} />}
      {edit && <ApplianceDialog hubId={hub.id} types={d.types} item={edit.id ? edit : null} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
